// מסך 5 — בדיקת חילוץ מפרט: כל שדה עם ערך או "לא ידוע", מקורות, השוואה לגרסה קודמת, סתירות, הגשה ואישור.
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { REQUIRED_FOR_DELIVERY, SPEC_FIELD_LABELS, SpecFieldKey } from '@bb/schema';
import { api } from '../api';
import { ErrorBox, Loading, fmtDate, useLoad, useMe } from '../components/ui';
import { PageHead, SpecStatusBadge, confirmAction, fmtValue, useAction } from '../components/mgmt/common';
import { SPEC_UNITS, SpecValueInput } from '../components/mgmt/SpecRuleEditor';
import type { Site } from '../components/mgmt/MediaParts';

interface RuleSource { sourceId: string; page: number | null; quote: string; confidence: number; method: 'text' | 'ocr' | 'ai' | 'manual' }
interface Rule { key: SpecFieldKey; value: unknown; unit: string | null; sources: RuleSource[] }
interface Conflict { key: SpecFieldKey; values: { value: unknown; sources: RuleSource[] }[]; resolved?: boolean; chosen?: unknown }
interface SpecVersion { id: string; placement_id: string; version: number; status: string; is_demo: boolean; conflicts: Conflict[]; rules: Rule[]; resolved: Record<string, unknown>; approved_at: string | null; created_at: string; updated_at: string }
interface SpecData { current: SpecVersion; previousVersion: number | null; changes: { key: string; before: unknown; after: unknown }[]; sources: { id: string; kind: string; purpose: string; url: string | null; status: string; error: string | null; fetched_at: string | null }[] }

const METHOD_L: Record<string, string> = { text: 'טקסט', ocr: 'OCR', ai: 'חילוץ AI', manual: 'ידני' };
const KEYS = SpecFieldKey.options;

export default function SpecReview() {
  const { id } = useParams();
  const me = useMe();
  const nav = useNavigate();
  const { data, error, loading, reload } = useLoad<SpecData>(`/api/spec-versions/${id}`);
  const sites = useLoad<Site[]>('/api/media-sites');
  const [editing, setEditing] = useState<SpecFieldKey | null>(null);
  const [filter, setFilter] = useState<'all' | 'unknown' | 'changed' | 'conflict'>('all');
  const ruleAct = useAction();
  const flow = useAction();

  const ctx = useMemo(() => {
    if (!data || !sites.data) return null;
    for (const s of sites.data) { const p = s.placements.find((x) => x.id === data.current.placement_id); if (p) return { site: s, placement: p }; }
    return null;
  }, [data, sites.data]);

  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorBox error={error} />;
  if (!data) return null;
  const v = data.current;
  const editable = me.role !== 'viewer' && (v.status === 'draft' || v.status === 'pending_approval');
  const openConflicts = (v.conflicts ?? []).filter((c) => !c.resolved);
  const missing = REQUIRED_FOR_DELIVERY.filter((k) => v.resolved[k] === 'unknown');
  const srcById = Object.fromEntries(data.sources.map((s) => [s.id, s]));
  const changes = data.previousVersion ? data.changes : [];
  const changed = new Set(changes.map((c) => c.key));

  const setRule = async (key: SpecFieldKey, value: unknown, note: string) => {
    const r = await ruleAct.run(() => api.put(`/api/spec-versions/${v.id}/rules/${key}`, { value, unit: SPEC_UNITS[key] ?? null, note }), `"${SPEC_FIELD_LABELS[key]}" נשמר`);
    if (r) { setEditing(null); reload(); }
  };
  const submit = async () => { const r = await flow.run(() => api.post(`/api/spec-versions/${v.id}/submit`), 'הוגש לאישור'); if (r) reload(); };
  const approve = async () => {
    if (!confirmAction(`לאשר את גרסה ${v.version}? היא תהפוך למפרט הנוכחי של המיקום, הגרסה הקודמת תסומן "הוחלף", ובאנרים שננעלו לגרסה ישנה יסומנו לבדיקה.`)) return;
    const r = await flow.run(() => api.post(`/api/spec-versions/${v.id}/approve`), 'המפרט אושר');
    if (r) reload();
  };
  const newDraft = async () => {
    const r = await flow.run(() => api.post<{ id: string }>(`/api/placements/${v.placement_id}/spec-versions`, { copyFrom: v.id }), 'נוצרה טיוטה חדשה');
    if (r) nav(`/spec-versions/${r.id}`);
  };

  const keys = KEYS.filter((k) => {
    if (filter === 'unknown') return v.resolved[k] === 'unknown';
    if (filter === 'changed') return changed.has(k);
    if (filter === 'conflict') return (v.conflicts ?? []).some((c) => c.key === k);
    return true;
  });

  return (
    <div>
      <PageHead title={`בדיקת מפרט — גרסה ${v.version}`}
        crumbs={<><Link to="/media">אתרים ומפרטים</Link>{ctx && <> / {ctx.site.name} / {ctx.placement.name}</>}</>}
        sub={<>נוצר {fmtDate(v.created_at)}{v.approved_at && ` · אושר ${fmtDate(v.approved_at)}`} · {data.previousVersion ? `השוואה לגרסה ${data.previousVersion}` : 'אין גרסה מאושרת קודמת להשוואה'}</>}>
        <SpecStatusBadge status={v.status} isDemo={v.is_demo} />
      </PageHead>

      {v.is_demo && <div className="alert warn" style={{ marginBottom: 10 }}><strong>מפרט לדוגמה — הדגמה בלבד.</strong> אינו מבוסס על הוראות אמיתיות של אתר ואין להשתמש בו למסירה.</div>}
      {openConflicts.length > 0 && <div className="alert err" style={{ marginBottom: 10 }} role="alert">יש {openConflicts.length} סתירות פתוחות בין מקורות ({openConflicts.map((c) => SPEC_FIELD_LABELS[c.key]).join(', ')}). יש להכריע בכל אחת לפני אישור.</div>}
      {missing.length > 0 && <div className="alert warn" style={{ marginBottom: 10 }}>שדות חובה למסירה שעדיין לא ידועים: {missing.map((k) => SPEC_FIELD_LABELS[k]).join(', ')}. ללא ערך יתאפשר יצוא טיוטה בלבד.</div>}

      <div className="card row between" style={{ marginBottom: 12 }}>
        <div className="row">
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center' }}><span>הצגה:</span>
            <select value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
              <option value="all">כל השדות ({KEYS.length})</option>
              <option value="unknown">לא ידועים ({KEYS.filter((k) => v.resolved[k] === 'unknown').length})</option>
              <option value="changed">השתנו מול הגרסה הקודמת ({changes.length})</option>
              <option value="conflict">סתירות ({(v.conflicts ?? []).length})</option>
            </select>
          </label>
        </div>
        <div className="row">
          {v.status === 'draft' && me.role !== 'viewer' && <button className="btn" onClick={submit} disabled={flow.busy}>הגשה לאישור</button>}
          {(v.status === 'draft' || v.status === 'pending_approval') && (me.canApproveSpecs
            ? <button className="btn primary" onClick={approve} disabled={flow.busy}>{flow.busy ? '…' : 'אישור המפרט'}</button>
            : <span className="small muted">אישור דורש הרשאת "אישור מפרטים"</span>)}
          {(v.status === 'approved' || v.status === 'superseded') && me.role !== 'viewer' && <button className="btn" onClick={newDraft} disabled={flow.busy}>טיוטה חדשה מגרסה זו</button>}
        </div>
      </div>
      <ErrorBox error={flow.error} />
      {!editable && v.status !== 'draft' && v.status !== 'pending_approval' && <div className="small muted" style={{ marginBottom: 8 }}>גרסה {v.status === 'approved' ? 'מאושרת' : 'שהוחלפה'} אינה ניתנת לעריכה. לשינוי צרו טיוטה חדשה.</div>}
      <div aria-live="polite"><ErrorBox error={ruleAct.error} /></div>

      <div className="rule-grid">
        {keys.map((k) => {
          const rule = v.rules.find((r) => r.key === k);
          const val = v.resolved[k];
          const unknown = val === 'unknown';
          const conflict = (v.conflicts ?? []).find((c) => c.key === k);
          const ch = changes.find((c) => c.key === k);
          return (
            <article key={k} className={`rule-card ${ch ? 'changed' : ''} ${conflict && !conflict.resolved ? 'conflict' : ''}`} aria-labelledby={`rk-${k}`}>
              <div className="row between">
                <h3 id={`rk-${k}`} style={{ margin: 0 }}>{SPEC_FIELD_LABELS[k]}{REQUIRED_FOR_DELIVERY.includes(k) && <span className="small muted"> · חובה למסירה</span>}</h3>
                <span className="mono small muted">{k}</span>
              </div>
              <div style={{ margin: '6px 0' }}>
                {unknown ? <span className="unknown">לא ידוע</span> : (
                  <span dir={/^[\x00-\x7F×]*$/.test(fmtValue(val)) ? 'ltr' : undefined} style={{ unicodeBidi: 'isolate', display: 'inline-block' }}>
                    <strong>{fmtValue(val)}</strong>{rule?.unit && <span className="small muted"> {rule.unit}</span>}
                  </span>
                )}
              </div>
              {ch && <div className="small"><span className="badge info">השתנה</span> לפני: <span className="diff-before">{fmtValue(ch.before)}</span> ← אחרי: <span className="diff-after">{fmtValue(ch.after)}</span></div>}

              {rule && rule.sources.length > 0 && (
                <details style={{ marginTop: 6 }} open={rule.sources.length <= 2}>
                  <summary className="small">מקורות ({rule.sources.length})</summary>
                  <SourceList sources={rule.sources} srcById={srcById} />
                </details>
              )}
              {!rule && !conflict && <div className="small muted">לא נמצא במקורות.</div>}

              {conflict && (
                <div style={{ marginTop: 8 }}>
                  <div className="row"><span className={`badge ${conflict.resolved ? 'ok' : 'err'}`}>{conflict.resolved ? 'סתירה הוכרעה' : 'סתירה בין מקורות'}</span>
                    {conflict.resolved && <span className="small">נבחר: <strong>{fmtValue(conflict.chosen)}</strong></span>}</div>
                  <div className="conflict-sides" style={{ marginTop: 6 }}>
                    {conflict.values.map((cv, i) => (
                      <div className="conflict-side" key={i}>
                        <strong className="ltr">{fmtValue(cv.value)}</strong>
                        <SourceList sources={cv.sources} srcById={srcById} />
                        {editable && <button className="btn small primary" disabled={ruleAct.busy} onClick={() => setRule(k, cv.value, `נבחר מתוך סתירה: ${describeSources(cv.sources, srcById)}`.slice(0, 400))}>בחירת ערך זה</button>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {editable && editing !== k && (
                <div className="row" style={{ marginTop: 8 }}>
                  <button className="btn small" onClick={() => setEditing(k)}>{conflict && !conflict.resolved ? 'הזנה ידנית' : 'עריכה ידנית'}</button>
                  {!unknown && <button className="btn small" disabled={ruleAct.busy} onClick={() => { if (confirmAction(`לסמן את "${SPEC_FIELD_LABELS[k]}" כלא ידוע?`)) setRule(k, null, 'סומן כלא ידוע ידנית'); }}>סימון כלא ידוע</button>}
                </div>
              )}
              {editing === k && <SpecValueInput fieldKey={k} initial={unknown ? null : val} busy={ruleAct.busy} onCancel={() => setEditing(null)} onSubmit={(value, note) => setRule(k, value, note)} />}
            </article>
          );
        })}
      </div>

      {data.sources.length > 0 && (
        <section className="card" style={{ marginTop: 12 }} aria-labelledby="srcs-h">
          <h2 id="srcs-h" style={{ marginTop: 0 }}>מקורות הגרסה</h2>
          <ul className="list-plain">{data.sources.map((s) => (
            <li key={s.id}><span className="badge">{s.kind}</span> {s.purpose === 'reference' ? 'רפרנס' : 'מפרט'} {s.url && <span className="url-cell" style={{ display: 'inline' }}>{s.url}</span>} <span className="small muted">{s.fetched_at ? `נמשך ${fmtDate(s.fetched_at)}` : ''}</span> {s.status === 'failed' && <span className="badge err">נכשל: {s.error}</span>}</li>
          ))}</ul>
        </section>
      )}
    </div>
  );
}

function SourceList({ sources, srcById }: { sources: RuleSource[]; srcById: Record<string, { kind: string; url: string | null }> }) {
  return (
    <ul className="list-plain">
      {sources.map((s, i) => {
        const src = srcById[s.sourceId];
        return (
          <li key={i} className="small" style={{ padding: '4px 0' }}>
            <div className="row" style={{ gap: 4 }}>
              <span className="badge">{s.sourceId === 'manual' ? 'ידני' : src ? (src.kind === 'pdf' ? 'PDF' : src.kind === 'url' ? 'קישור' : 'טקסט') : 'מקור'}</span>
              {s.page != null && <span>עמ' {s.page}</span>}
              <span className={`badge ${s.confidence >= 0.8 ? 'ok' : s.confidence >= 0.5 ? 'warn' : 'err'}`}>ביטחון {Math.round(s.confidence * 100)}%</span>
              <span className="muted">{METHOD_L[s.method] ?? s.method}</span>
            </div>
            {src?.url && <div className="url-cell muted">{src.url}</div>}
            {s.quote && <div className="source-quote">"{s.quote}"</div>}
          </li>
        );
      })}
    </ul>
  );
}

function describeSources(sources: RuleSource[], srcById: Record<string, { kind: string; url: string | null }>) {
  return sources.map((s) => `${srcById[s.sourceId]?.kind ?? s.sourceId}${s.page != null ? ` עמ' ${s.page}` : ''}`).join(', ');
}
